/**
 * commonLinks.service.ts — Admin > Common Links: the links shown on users'
 * dashboards (dashboard_entries), edited through /v5/dashboard-entries as the
 * corporate dashboard does. Deleted = endDate set; restore clears it. Writes
 * need Admin Access. (The dashboard itself reads dashboard.service's
 * getCommonLinks.)
 *
 * From legacy's page (admin_dashboard_list.php), beyond corp: a link's image
 * (jpg / png, kept in the account's document folder where legacy's dashboard
 * reads it) and the links' order (deOrder, dragged on the list).
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

/** dashboard_entries.status: who sees the link. */
export const LINK_VISIBILITY_OPTIONS = [
  { value: 0, label: 'Everyone' },
  { value: 1, label: 'Admin Only' },
];

export interface AdminCommonLink {
  dashboardEntryID: number;
  entryTitle: string;
  entryURL: string;
  entryContent: string;
  /** 0 = everyone, 1 = admins only. */
  status: number;
  /** The image's file name in the account's dashboard folder; '' = none. */
  entryImage: string;
  /** deOrder. */
  order: number;
  active: boolean;
}

export interface CommonLinkPayload {
  entryTitle: string;
  entryURL: string;
  entryContent: string;
  status: number;
  /** Sent only when creating (always ''): Admin has no image field (corp parity), so an edit keeps the link's image. */
  entryImage?: string;
}

/** Every link, deleted ones too, in the account's order. */
export function listAdminCommonLinks(): Promise<AdminCommonLink[]> {
  return apiClient
    .get<ApiResponse<Record<string, unknown>[]>>('/dashboard-entries', { params: { include_inactive: 1 } })
    .then((r) =>
      (Array.isArray(r.data?.data) ? r.data.data : []).map((row) => ({
        dashboardEntryID: Number(row.dashboardEntryID ?? 0),
        entryTitle: String(row.entryTitle ?? ''),
        entryURL: String(row.entryURL ?? ''),
        entryContent: String(row.entryContent ?? ''),
        status: Number(row.status ?? 0) === 1 ? 1 : 0,
        entryImage: String(row.entryImage ?? ''),
        order: Number(row.deOrder ?? 0),
        active: row.active === true || row.active === 1 || row.active === '1',
      })),
    );
}

// The columns are NOT NULL: every field is sent, as a string, never null.
export function createCommonLink(data: CommonLinkPayload): Promise<void> {
  return apiClient.post('/dashboard-entries', data).then(() => undefined);
}

export function updateCommonLink(id: number, data: CommonLinkPayload): Promise<void> {
  return apiClient.put(`/dashboard-entries/${id}`, data).then(() => undefined);
}

export function deleteCommonLink(id: number): Promise<void> {
  return apiClient.delete(`/dashboard-entries/${id}`).then(() => undefined);
}

export function restoreCommonLink(id: number): Promise<void> {
  return apiClient.post(`/dashboard-entries/${id}/restore`).then(() => undefined);
}

/** The links in their new order (deOrder 1, 2, …). */
export function reorderCommonLinks(ids: number[]): Promise<void> {
  return apiClient.put('/dashboard-entries/reorder', { ids }).then(() => undefined);
}

// ─── Image ─────────────────────────────────────────────────────────────────────

/**
 * An object URL for a link's image: the saved one, or (with fileName) one just
 * uploaded and not saved on the link yet. Null when there is none to show.
 * The caller revokes it.
 */
export async function fetchCommonLinkImageUrl(id: number, fileName?: string): Promise<string | null> {
  const r = await apiClient.get<{ contentType?: string; data?: string }>(`/dashboard-entries/${id || 0}/image`, {
    params: fileName ? { file: fileName } : undefined,
  });
  const { contentType, data } = r.data ?? {};
  if (typeof data !== 'string' || typeof contentType !== 'string' || !contentType.startsWith('image/')) return null;
  const bytes = Uint8Array.from(atob(data), (c) => c.charCodeAt(0));
  if (bytes.length === 0) return null;
  return URL.createObjectURL(new Blob([bytes], { type: contentType }));
}
